import { produce } from 'immer'
import { ExpenseReportResponse } from 'src/models/expenseReport.model'
import {
  DELETE_EXPENSE_SUCCESS,
  DeleteExpenseActions,
  PATCH_REIMBURSEMENT_MANAGER_APPROVAL,
  PATCH_UPDATE_EXPENSE_REPORT_SUCCESS,
  PatchReimbursementManagerApprovalActions,
  PatchUpdateExpenseReportActions,
  POST_APPROVE_EXPENSES_SUCCESS,
  POST_MARK_EXPENSE_REPORTS_PAID_SUCCESS,
  POST_REJECT_EXPENSES_SUCCESS,
  POST_SUBMIT_EXPENSE_REPORT_FAILURE,
  POST_SUBMIT_EXPENSE_REPORT_INIT,
  POST_SUBMIT_EXPENSE_REPORT_SUCCESS,
  PostApproveExpensesActions,
  PostMarkExpenseReportsPaidActions,
  PostRejectExpensesActions,
  PostSubmitExpenseReportActions,
  RESET_BULK_SELECT,
  ResetBulkSelectAction,
} from 'src/redux/actions/data/expenses.actions'

// NOTE (EX-39746): the report-detail READ was migrated to React Query, so the
// GET_EXPENSE_REPORT_* / CLEAR_EXPENSE_REPORT cases and the `loaded` flag are
// gone. The `expenseReport` field + the mutation cases below remain redux for a
// stacked mutations follow-up; the React Query report-detail cache is kept fresh
// by the interop reducer (src/queries/redux/reactQueryInterop.reducer.ts).
export interface ExpenseReportState {
  expenseReport?: ExpenseReportResponse
  postSubmittingExpenseReport: boolean
  postSubmittingExpenseReportError?: string
  resetBulkSelect: boolean
}

export const initialState: ExpenseReportState = {
  postSubmittingExpenseReport: false,
  resetBulkSelect: false,
}

export type Actions =
  | PostApproveExpensesActions
  | PostRejectExpensesActions
  | PostSubmitExpenseReportActions
  | PostMarkExpenseReportsPaidActions
  | DeleteExpenseActions
  | ResetBulkSelectAction
  | PatchUpdateExpenseReportActions
  | PatchReimbursementManagerApprovalActions

const CoreExpenseReportReducer = (
  state = initialState,
  action: Actions,
): ExpenseReportState => {
  return produce(state, (draft) => {
    switch (action.type) {
      case POST_APPROVE_EXPENSES_SUCCESS: {
        if (draft.expenseReport !== undefined) {
          draft.expenseReport = action.payload
        }
        return draft
      }

      case POST_REJECT_EXPENSES_SUCCESS: {
        if (draft.expenseReport !== undefined) {
          draft.expenseReport = action.payload
        }
        return draft
      }

      case POST_SUBMIT_EXPENSE_REPORT_INIT: {
        draft.postSubmittingExpenseReport = true
        draft.postSubmittingExpenseReportError = undefined
        return draft
      }

      case POST_SUBMIT_EXPENSE_REPORT_SUCCESS: {
        draft.postSubmittingExpenseReport = false
        draft.expenseReport = action.payload
        draft.resetBulkSelect = true

        return draft
      }

      case POST_SUBMIT_EXPENSE_REPORT_FAILURE: {
        draft.postSubmittingExpenseReport = false
        draft.postSubmittingExpenseReportError = action.payload.error
        return draft
      }

      case POST_MARK_EXPENSE_REPORTS_PAID_SUCCESS: {
        draft.postSubmittingExpenseReport = false
        draft.expenseReport = {
          ...action.payload.expenseReports[0],
          expenses: draft.expenseReport?.expenses,
        }

        return draft
      }

      case DELETE_EXPENSE_SUCCESS: {
        draft.expenseReport = action.payload
        draft.resetBulkSelect = true

        return draft
      }

      case RESET_BULK_SELECT: {
        draft.resetBulkSelect = false

        return draft
      }

      case PATCH_UPDATE_EXPENSE_REPORT_SUCCESS: {
        if (draft.expenseReport)
          draft.expenseReport.reportName = action.payload.reportName
        return draft
      }

      case PATCH_REIMBURSEMENT_MANAGER_APPROVAL: {
        action.payload.forEach((curr) => {
          const expenseIndex = draft.expenseReport?.expenses?.findIndex(
            (e) => e.id === curr.id,
          )
          if (
            expenseIndex !== -1 &&
            expenseIndex !== undefined &&
            draft.expenseReport !== undefined &&
            draft.expenseReport.expenses !== undefined
          ) {
            draft.expenseReport.expenses[expenseIndex] = {
              ...draft.expenseReport.expenses[expenseIndex],
              managerReviewStatus: curr.managerReviewStatus,
              managerId: curr.managerId,
            }
          }
        })
        return draft
      }
    }
  })
}

const ExpenseReportReducer = CoreExpenseReportReducer

export default ExpenseReportReducer
